/**
 * V1 자산 필터링 유틸리티
 *
 * V2 API에서 반환된 자산 중 V1에서 지원하는 자산만 필터링합니다.
 *
 * @see /docs/musicgi-v2/filter/V1_ASSET_FILTERING_DESIGN.md
 */

import {
  V1_SUPPORTED_CURRENCY_IDS,
  V1_SUPPORTED_TOKEN_TYPES,
  HIDDEN_FROM_ADD_ACCOUNT_CURRENCY_IDS,
  HIDDEN_FROM_ADD_ACCOUNT_TOKEN_TYPES,
} from '@/networks/api/v2/constants/v1-v2-mappings'

/**
 * V1에서 지원하는 코인/네트워크인지 확인
 *
 * NFT도 이 함수로 네트워크만 확인하면 됨 (컨트랙트 매핑 불필요)
 *
 * @param currencyId V2 Currency ID (예: "ETHEREUM", "KLAYTN", "CHAN:42161")
 * @returns V1 지원 여부
 *
 * @example
 * isV1SupportedCoin('ETHEREUM')     // true
 * isV1SupportedCoin('KLAYTN')       // true
 * isV1SupportedCoin('APTOS')        // false (V1 미지원)
 * isV1SupportedCoin('evmMetis')     // false (V1 미지원)
 */
export function isV1SupportedCoin(currencyId: string): boolean {
  return V1_SUPPORTED_CURRENCY_IDS.has(currencyId)
}

/**
 * V1에서 지원하는 토큰 타입인지 확인
 *
 * @param deviceStoreId V2 토큰 타입 (예: "ERC20", "KLAYTN-ERC20", "CH20:42161")
 * @returns V1 지원 여부
 *
 * @example
 * isV1SupportedTokenType('ERC20')           // true
 * isV1SupportedTokenType('KLAYTN-ERC20')    // true
 * isV1SupportedTokenType('APTOS-TOKEN')     // false (V1 미지원)
 */
export function isV1SupportedTokenType(deviceStoreId: string): boolean {
  return V1_SUPPORTED_TOKEN_TYPES.has(deviceStoreId)
}

/**
 * 코인 목록에서 V1 지원 항목만 필터링
 *
 * @param coins 코인 목록 (currencyId 속성 필요)
 * @returns V1 지원 코인만 포함된 배열
 *
 * @example
 * const allCoins = [{ currencyId: 'ETHEREUM' }, { currencyId: 'APTOS' }]
 * filterV1SupportedCoins(allCoins)
 * // => [{ currencyId: 'ETHEREUM' }]
 */
export function filterV1SupportedCoins<T extends { currencyId: string }>(
  coins: T[],
): T[] {
  return coins.filter((coin) => isV1SupportedCoin(coin.currencyId))
}

/**
 * 토큰 목록에서 V1 지원 항목만 필터링
 *
 * 부모 네트워크와 토큰 타입 모두 V1 지원인지 확인
 *
 * @param tokens 토큰 목록
 * @returns V1 지원 토큰만 포함된 배열
 *
 * @example
 * const allTokens = [
 *   { deviceStoreId: 'ERC20', parentCurrencyId: 'ETHEREUM' },
 *   { deviceStoreId: 'APTOS-TOKEN', parentCurrencyId: 'APTOS' }
 * ]
 * filterV1SupportedTokens(allTokens)
 * // => [{ deviceStoreId: 'ERC20', parentCurrencyId: 'ETHEREUM' }]
 */
export function filterV1SupportedTokens<
  T extends { deviceStoreId?: string; parentCurrencyId?: string },
>(tokens: T[]): T[] {
  return tokens.filter((token) => {
    // 부모 코인이 V1 지원인지 확인
    if (token.parentCurrencyId && !isV1SupportedCoin(token.parentCurrencyId)) {
      return false
    }
    // 토큰 타입이 V1 지원인지 확인
    if (token.deviceStoreId && !isV1SupportedTokenType(token.deviceStoreId)) {
      return false
    }
    return true
  })
}

/**
 * 계정 목록에서 V1 지원 네트워크의 계정만 필터링
 *
 * @param accounts 계정 목록 (currencyId 속성 필요)
 * @returns V1 지원 네트워크의 계정만 포함된 배열
 *
 * @example
 * const allAccounts = [
 *   { id: '1', currencyId: 'ETHEREUM' },
 *   { id: '2', currencyId: 'APTOS' }
 * ]
 * filterV1SupportedAccounts(allAccounts)
 * // => [{ id: '1', currencyId: 'ETHEREUM' }]
 */
export function filterV1SupportedAccounts<T extends { currencyId: string }>(
  accounts: T[],
): T[] {
  return accounts.filter((account) => isV1SupportedCoin(account.currencyId))
}

/**
 * V2 토큰 ID에서 토큰 타입(deviceStoreId)을 추출하여 V1 지원 여부 확인
 *
 * V2 토큰 ID 형식: "{deviceStoreId}/{contractAddress}"
 *
 * @param v2TokenId V2 토큰 ID (예: "ERC20/0x...")
 * @returns V1 지원 여부
 *
 * @example
 * isV1SupportedTokenById('ERC20/0x1234...')        // true
 * isV1SupportedTokenById('KLAYTN-ERC20/0x5678...') // true
 * isV1SupportedTokenById('APTOS-TOKEN/0xabcd...')  // false
 */
export function isV1SupportedTokenById(v2TokenId: string): boolean {
  const [deviceStoreId] = v2TokenId.split('/')
  if (!deviceStoreId) {
    return false
  }
  return isV1SupportedTokenType(deviceStoreId)
}

/**
 * 계정 추가 UI에서 숨겨야 하는 코인/네트워크인지 확인
 *
 * 이미 생성된 계정은 표시되지만, 새 계정 추가 시에는 숨김
 *
 * @param currencyId V2 Currency ID (예: "near_pt", "near_pt_testnet")
 * @returns 숨김 여부
 *
 * @example
 * isHiddenFromAddAccount('near_pt')          // true
 * isHiddenFromAddAccount('near_pt_testnet')  // true
 * isHiddenFromAddAccount('NEAR')             // false
 * isHiddenFromAddAccount('ETHEREUM')         // false
 */
export function isHiddenFromAddAccount(currencyId: string): boolean {
  return HIDDEN_FROM_ADD_ACCOUNT_CURRENCY_IDS.has(currencyId)
}

/**
 * 계정 추가 UI에서 숨겨야 하는 토큰 타입인지 확인
 *
 * @param deviceStoreId V2 토큰 타입 (예: "near_pt_token", "near_pt_testnet_token")
 * @returns 숨김 여부
 *
 * @example
 * isHiddenTokenTypeFromAddAccount('near_pt_token')          // true
 * isHiddenTokenTypeFromAddAccount('near_pt_testnet_token')  // true
 * isHiddenTokenTypeFromAddAccount('NEAR-TOKEN')             // false
 * isHiddenTokenTypeFromAddAccount('ERC20')                  // false
 */
export function isHiddenTokenTypeFromAddAccount(deviceStoreId: string): boolean {
  return HIDDEN_FROM_ADD_ACCOUNT_TOKEN_TYPES.has(deviceStoreId)
}
